import { useEffect, useMemo, useRef, useState } from "react";
import { getAppCopy } from "../content/appLocale";
import "./TrustedPeopleBuilderPanel.css";

const INVITE_RESEND_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000;

function relationDisplayLabel(relation, copy) {
  const map = {
    Owner: copy.ownerRelation || "Owner",
    Parent: copy.relationshipOptions[0],
    Spouse: copy.relationshipOptions[1],
    Partner: copy.relationshipOptions[2],
    "Ex-spouse": copy.relationshipOptions[3],
    Sibling: copy.relationshipOptions[4],
    Child: copy.relationshipOptions[5],
    Grandchild: copy.relationshipOptions[6],
    Friend: copy.relationshipOptions[7],
    "Legal Advisor": copy.relationshipOptions[8],
  };
  return map[relation] || relation;
}

function memberInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (!parts.length) {
    return "?";
  }
  return parts.map((part) => part.charAt(0).toUpperCase()).join("");
}

function memberStatusLabel(member, copy) {
  if (member.relation === "Owner") {
    return copy.statusYou;
  }
  if (member.inviteAcceptedAt) {
    return copy.statusConnected;
  }
  if (member.linkedUserId || member.inviteStatus === "existing-user") {
    return copy.statusReadyToConnect;
  }
  if (member.inviteStatus === "invite-requested") {
    return copy.statusInviteSent;
  }
  if (member.inviteStatus === "invite-cancelled") {
    return copy.statusInviteCancelled;
  }
  return copy.statusNotInvited;
}

function memberTrustBadge(member, copy) {
  if (member.relation === "Owner") {
    return copy.badgePlanOwner;
  }
  if (!member.email && !member.inviteAcceptedAt && member.inviteStatus !== "invite-requested") {
    return copy.badgeNoAccessYet;
  }
  if (member.relation === "Spouse" || member.relation === "Partner") {
    return copy.badgePrimaryTrusted;
  }
  return copy.badgeFamily;
}

function memberSupportCopy(member, copy) {
  if (member.relation === "Owner") {
    return copy.supportOwner;
  }
  if (member.inviteAcceptedAt) {
    return copy.supportConnected;
  }
  if (member.linkedUserId || member.inviteStatus === "existing-user") {
    return copy.supportExistingUser;
  }
  if (member.inviteStatus === "invite-requested") {
    return copy.supportInviteRequested;
  }
  if (member.inviteStatus === "invite-cancelled") {
    return copy.supportInviteCancelled;
  }
  if (member.email) {
    return copy.supportEmailReady;
  }
  return copy.supportNoAccess;
}

function relationshipHint(relation, copy) {
  return copy.relationshipHints[relation] || copy.relationshipHints.default;
}

function formatMemberUpdatedAt(member, copy, locale = "en") {
  const iso = member.updatedAt || member.createdAt;
  if (!iso) {
    return copy.addedRecently;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return copy.addedRecently;
  }
  return `${copy.updatedPrefix} ${new Intl.DateTimeFormat(locale === "fr" ? "fr-CA" : "en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date)}`;
}

function formatInviteDate(iso, locale = "en") {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat(locale === "fr" ? "fr-CA" : "en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function getInviteLastSentAt(member) {
  return member.inviteLastSentAt || member.inviteRequestedAt || null;
}

function getInviteResendAvailableAt(member) {
  if (member.inviteResendAvailableAt) {
    return member.inviteResendAvailableAt;
  }
  const lastSentAt = getInviteLastSentAt(member);
  if (!lastSentAt) {
    return null;
  }
  const lastDate = new Date(lastSentAt);
  if (Number.isNaN(lastDate.getTime())) {
    return null;
  }
  return new Date(lastDate.getTime() + INVITE_RESEND_COOLDOWN_MS).toISOString();
}

function canResendInvite(member) {
  if (member.inviteStatus !== "invite-requested") {
    return false;
  }
  if (member.inviteResendReady === false) {
    return false;
  }
  const availableAt = getInviteResendAvailableAt(member);
  if (!availableAt) {
    return true;
  }
  const availableDate = new Date(availableAt);
  return !Number.isNaN(availableDate.getTime()) && Date.now() >= availableDate.getTime();
}

function inviteHoverCopy(member, copy, locale = "en") {
  const details = [];
  const firstSent = formatInviteDate(member.inviteFirstSentAt || member.inviteRequestedAt, locale);
  const lastSent = formatInviteDate(getInviteLastSentAt(member), locale);
  const resent = formatInviteDate(member.inviteResentAt, locale);
  const available = formatInviteDate(getInviteResendAvailableAt(member), locale);
  if (firstSent) {
    details.push(`${copy.firstSent} ${firstSent}`);
  }
  if (lastSent) {
    details.push(`${copy.lastSent} ${lastSent}`);
  }
  if (resent) {
    details.push(`${copy.lastResent} ${resent}`);
  }
  if (available) {
    details.push(`${copy.resendAvailable} ${available}`);
  }
  details.push(copy.resendRule);
  return details.join(" • ");
}

function TrustedPeopleMemberCard({
  member,
  locale,
  copy,
  isEditingEmail,
  editingFamilyEmailValue,
  setEditingFamilyEmailValue,
  familyEmailSavingId,
  beginFamilyEmailEdit,
  cancelFamilyEmailEdit,
  saveFamilyMemberEmail,
  askToJoin,
  resendInvite,
  cancelInvite,
  familyInviteBusyId,
  requestDeleteFamilyMember,
}) {
  const canRemove =
    member.relation !== "Owner" && member.backendNodeId && member.backendNodeId !== "owner-node";
  const trustBadge = memberTrustBadge(member, copy);
  const statusLabel = memberStatusLabel(member, copy);
  const invitePending = member.inviteStatus === "invite-requested";
  const inviteCancelled = member.inviteStatus === "invite-cancelled";
  const sendInviteAvailable =
    member.email &&
    member.relation !== "Owner" &&
    !member.inviteAcceptedAt &&
    !invitePending;
  const resendAvailable = invitePending && canResendInvite(member);
  const resendAvailableAt = formatInviteDate(getInviteResendAvailableAt(member), locale);
  const inviteDetails =
    invitePending || inviteCancelled ? inviteHoverCopy(member, copy, locale) : "";
  const inviteBusy = familyInviteBusyId === member.backendNodeId;

  return (
    <article className="trusted-person-card">
      <div className="trusted-person-card__header">
        <div className="trusted-person-card__avatar" aria-hidden="true">
          {memberInitials(member.name)}
        </div>
        <div className="trusted-person-card__identity">
          <h4>{member.name}</h4>
          <div className="trusted-person-card__badges">
            <span className="trusted-person-card__badge trusted-person-card__badge--relationship">
              {relationDisplayLabel(member.relation, copy)}
            </span>
            <span
              className={`trusted-person-card__badge trusted-person-card__badge--trust ${
                trustBadge === copy.badgePrimaryTrusted
                  ? "is-primary"
                  : trustBadge === copy.badgeNoAccessYet
                    ? "is-muted"
                    : ""
              }`}
            >
              {trustBadge}
            </span>
          </div>
        </div>
        <span
          className={`trusted-person-card__status ${
            member.inviteAcceptedAt
              ? "is-connected"
              : invitePending
                ? "is-pending"
                : inviteCancelled
                  ? "is-cancelled"
                : ""
          }`}
          title={inviteDetails || undefined}
        >
          {statusLabel}
        </span>
      </div>

      <p className="trusted-person-card__support">{memberSupportCopy(member, copy)}</p>

      <div className="trusted-person-card__meta">
        <span>{formatMemberUpdatedAt(member, copy, locale)}</span>
        {member.email ? <span>{member.email}</span> : <span>{copy.noEmailYet}</span>}
      </div>

      {invitePending ? (
        <p className="trusted-person-card__invite-note" title={inviteDetails || undefined}>
          {resendAvailable
            ? `${copy.lastInviteSent} ${formatInviteDate(getInviteLastSentAt(member), locale) || copy.addedRecently}.`
            : `${copy.inviteResendAvailable} ${resendAvailableAt || copy.resendRule}.`}
        </p>
      ) : null}

      {inviteCancelled && member.inviteCancelledAt ? (
        <p className="trusted-person-card__invite-note" title={inviteDetails || undefined}>
          {copy.invitationCancelled} {formatInviteDate(member.inviteCancelledAt, locale)}.
        </p>
      ) : null}

      {isEditingEmail ? (
        <form
          className="trusted-person-card__editor"
          onSubmit={(event) => {
            event.preventDefault();
            saveFamilyMemberEmail(member);
          }}
        >
          <label className="sr-only" htmlFor={`trusted-email-${member.id}`}>
            Contact email for {member.name}
          </label>
          <input
            id={`trusted-email-${member.id}`}
            className="trusted-person-card__input"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={editingFamilyEmailValue}
            onChange={(event) => setEditingFamilyEmailValue(event.target.value)}
            placeholder="name@example.com"
            disabled={familyEmailSavingId === member.backendNodeId}
          />
          <div className="trusted-person-card__editor-actions">
            <button
              type="submit"
              className="small-btn"
              disabled={familyEmailSavingId === member.backendNodeId}
            >
              {familyEmailSavingId === member.backendNodeId ? copy.saving : copy.saveContact}
            </button>
            <button
              type="button"
              className="small-btn trusted-person-card__cancel"
              onClick={cancelFamilyEmailEdit}
              disabled={familyEmailSavingId === member.backendNodeId}
            >
              {copy.cancel}
            </button>
          </div>
        </form>
      ) : null}

      <div className="trusted-person-card__actions">
        {member.relation !== "Owner" ? (
          <button
            type="button"
            className="small-btn trusted-person-card__link-btn"
            onClick={() => beginFamilyEmailEdit(member)}
          >
            {copy.edit}
          </button>
        ) : null}
        {sendInviteAvailable ? (
          <button
            type="button"
            className="small-btn"
            onClick={() => askToJoin(member)}
            disabled={inviteBusy}
          >
            {inviteBusy ? copy.sending : inviteCancelled ? copy.sendInviteAgain : copy.sendInvite}
          </button>
        ) : null}
        {invitePending ? (
          <>
            <button
              type="button"
              className="small-btn trusted-person-card__link-btn"
              onClick={() => resendInvite(member)}
              disabled={!resendAvailable || inviteBusy}
              title={inviteDetails || undefined}
            >
              {inviteBusy ? copy.resending : copy.resendInvite}
            </button>
            <button
              type="button"
              className="small-btn trusted-person-card__cancel-invite"
              onClick={() => cancelInvite(member)}
              disabled={inviteBusy}
            >
              {inviteBusy ? copy.cancelling : copy.cancelInvitation}
            </button>
          </>
        ) : null}
        {canRemove ? (
          <button
            type="button"
            className="small-btn trusted-person-card__remove"
            onClick={() => requestDeleteFamilyMember(member)}
          >
            {copy.remove}
          </button>
        ) : null}
      </div>
    </article>
  );
}

function TrustedPeopleInvitationCard({
  invitation,
  acceptInvitation,
  familyInvitationBusyId,
  locale,
  copy,
}) {
  const busy = familyInvitationBusyId === invitation.invitationId;
  return (
    <article className="trusted-people-invitation-card">
      <div className="trusted-people-invitation-card__head">
        <div>
          <p className="trusted-people-invitation-card__eyebrow">{copy.pendingInvitationEyebrow}</p>
          <h4>{invitation.ownerName}</h4>
        </div>
        <span className="trusted-person-card__badge trusted-person-card__badge--relationship">
          {relationDisplayLabel(invitation.relation, copy)}
        </span>
      </div>
      <p className="trusted-people-invitation-card__body">
        {copy.pendingInvitationBody
          .replace("{ownerName}", invitation.ownerName)
          .replace("{inviteeName}", invitation.inviteeName)}
      </p>
      <div className="trusted-people-invitation-card__meta">
        {invitation.ownerEmail ? <span>{invitation.ownerEmail}</span> : null}
        {invitation.sentAt ? <span>{copy.sentPrefix} {formatInviteDate(invitation.sentAt, locale)}</span> : null}
      </div>
      <div className="trusted-people-invitation-card__actions">
        <button type="button" onClick={() => acceptInvitation(invitation)} disabled={busy}>
          {busy ? copy.connecting : copy.acceptInvitation}
        </button>
      </div>
    </article>
  );
}

export function TrustedPeopleBuilderPanel({
  locale = "en",
  familyMembers,
  familyInvitations,
  memberName,
  setMemberName,
  memberRelation,
  setMemberRelation,
  memberEmail,
  setMemberEmail,
  memberBirthYear,
  setMemberBirthYear,
  memberNickname,
  setMemberNickname,
  memberPronouns,
  setMemberPronouns,
  memberIdentityNote,
  setMemberIdentityNote,
  memberSpecialNote,
  setMemberSpecialNote,
  memberCoParentId,
  setMemberCoParentId,
  memberPartnerId,
  setMemberPartnerId,
  memberChartParentId,
  setMemberChartParentId,
  childBranchOptions,
  partnerLinkOptions,
  addFamilyMember,
  requestDeleteFamilyMember,
  askToJoin,
  editingFamilyEmailNodeId,
  editingFamilyEmailValue,
  setEditingFamilyEmailValue,
  familyEmailSavingId,
  beginFamilyEmailEdit,
  cancelFamilyEmailEdit,
  saveFamilyMemberEmail,
  familyInviteBusyId,
  familyInvitationBusyId,
  resendInvite,
  cancelInvite,
  acceptInvitation,
  planLimitPrompt,
  onStartTrial,
  onOpenPricing,
  planLimitCopy,
}) {
  const copy = getAppCopy(locale).trustedPeople;
  const [entryStep, setEntryStep] = useState(1);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [futureAccessIntent, setFutureAccessIntent] = useState(false);
  const nameInputRef = useRef(null);
  const relationshipOptions = useMemo(
    () => [
      { value: "Parent", label: copy.relationshipOptions[0] },
      { value: "Spouse", label: copy.relationshipOptions[1] },
      { value: "Partner", label: copy.relationshipOptions[2] },
      { value: "Ex-spouse", label: copy.relationshipOptions[3] },
      { value: "Sibling", label: copy.relationshipOptions[4] },
      { value: "Child", label: copy.relationshipOptions[5] },
      { value: "Grandchild", label: copy.relationshipOptions[6] },
      { value: "Friend", label: copy.relationshipOptions[7] },
      { value: "Legal Advisor", label: copy.relationshipOptions[8] },
    ],
    [copy.relationshipOptions]
  );

  const peopleOnly = useMemo(
    () => familyMembers.filter((member) => member.relation !== "Owner"),
    [familyMembers]
  );

  useEffect(() => {
    if (
      !memberName &&
      !memberEmail &&
      !memberBirthYear &&
      !memberNickname &&
      !memberIdentityNote &&
      !memberSpecialNote
    ) {
      setEntryStep(1);
      setDetailsOpen(false);
      setFutureAccessIntent(false);
    }
  }, [
    memberBirthYear,
    memberEmail,
    memberIdentityNote,
    memberName,
    memberNickname,
    memberSpecialNote,
  ]);

  const resetDraft = () => {
    setMemberName("");
    setMemberEmail("");
    setMemberBirthYear("");
    setMemberNickname("");
    setMemberPronouns("");
    setMemberIdentityNote("");
    setMemberSpecialNote("");
    setMemberCoParentId("");
    setMemberPartnerId("owner-node");
    setMemberChartParentId("");
    setMemberRelation("Parent");
    setEntryStep(1);
    setDetailsOpen(false);
    setFutureAccessIntent(false);
    window.requestAnimationFrame(() => {
      nameInputRef.current?.focus();
    });
  };

  const handleRelationChange = (next) => {
    setMemberRelation(next);
    if (next !== "Child") {
      setMemberCoParentId("");
    }
    if (next !== "Grandchild") {
      setMemberChartParentId("");
    }
    if (next === "Spouse" || next === "Partner") {
      setMemberPartnerId((prev) => prev || "owner-node");
    } else {
      setMemberPartnerId("owner-node");
    }
  };

  const canAdvanceFromName = Boolean(memberName.trim());
  const canSubmit =
    Boolean(memberName.trim()) &&
    Boolean(memberEmail.trim()) &&
    !(memberRelation === "Grandchild" && !memberChartParentId);

  return (
    <div className="trusted-people-stack">
      <section className="trusted-people-guidance" aria-label={copy.guidanceAria}>
        <div>
          <p className="trusted-people-kicker">{copy.guidanceKicker}</p>
          <h3>{copy.guidanceTitle}</h3>
          <p>{copy.guidanceBody}</p>
        </div>
        <ul className="trusted-people-guidance-list">
          {copy.guidancePoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>

      <div className="panel trusted-people-builder-panel">
        <div className="panel-head trusted-people-panel-head">
          <div className="trusted-people-panel-copy">
            <h2>{copy.panelTitle}</h2>
            <p>{copy.panelBody}</p>
          </div>
          <div className="trusted-people-panel-banner" role="note">
            <strong>{copy.panelBannerStrong}</strong> {copy.panelBannerTail}
          </div>
        </div>

        <div className="trusted-people-builder-grid">
          <div className="trusted-people-form-shell">
            {familyInvitations?.length ? (
              <div className="trusted-people-invitations" aria-label={copy.invitationsHead}>
                <div className="trusted-people-invitations__head">
                  <h3>{copy.invitationsHead}</h3>
                  <p>{copy.invitationsBody}</p>
                </div>
                <div className="trusted-people-invitations__grid">
                  {familyInvitations.map((invitation) => (
                    <TrustedPeopleInvitationCard
                      key={invitation.invitationId}
                      invitation={invitation}
                      acceptInvitation={acceptInvitation}
                      familyInvitationBusyId={familyInvitationBusyId}
                      locale={locale}
                      copy={copy}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            <div className="trusted-people-stepper" aria-label={copy.addPerson}>
              <span className={entryStep >= 1 ? "is-active" : ""}>{copy.stepName}</span>
              <span className={entryStep >= 2 ? "is-active" : ""}>{copy.stepRelationship}</span>
              <span className={entryStep >= 3 ? "is-active" : ""}>{copy.stepAccess}</span>
              <span className={detailsOpen ? "is-active" : ""}>{copy.stepDetails}</span>
            </div>

            <form className="trusted-people-form" onSubmit={addFamilyMember}>
                <section className="trusted-people-step-card is-current">
                  <div className="trusted-people-step-card__head">
                    <span className="trusted-people-step-number">{copy.step1}</span>
                    <h3>{copy.step1Title}</h3>
                  </div>
                  <label className="field-label" htmlFor="trusted-person-name">
                    {copy.name}
                  </label>
                  <input
                    id="trusted-person-name"
                    ref={nameInputRef}
                    type="text"
                    placeholder={copy.fullName}
                    value={memberName}
                    onChange={(event) => setMemberName(event.target.value)}
                  />
                <div className="trusted-people-inline-actions">
                  <button
                    type="button"
                    className="small-btn trusted-people-next"
                    disabled={!canAdvanceFromName}
                    onClick={() => setEntryStep(2)}
                  >
                    {copy.next}
                  </button>
                  <button
                    type="button"
                    className="small-btn trusted-people-secondary"
                    onClick={resetDraft}
                  >
                    {copy.continueLater}
                  </button>
                </div>
              </section>

              {entryStep >= 2 ? (
                <section className="trusted-people-step-card">
                  <div className="trusted-people-step-card__head">
                    <span className="trusted-people-step-number">{copy.step2}</span>
                    <h3>{copy.step2Title}</h3>
                  </div>
                  <label className="field-label" htmlFor="trusted-person-relation">
                    {copy.relationship}
                  </label>
                  <select
                    id="trusted-person-relation"
                    value={memberRelation}
                    onChange={(event) => handleRelationChange(event.target.value)}
                  >
                    {relationshipOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="trusted-people-hint">{relationshipHint(memberRelation, copy)}</p>

                  {memberRelation === "Child" ? (
                    <div className="trusted-people-conditional">
                      <label className="field-label" htmlFor="trusted-person-coparent">
                        {copy.withParent} <span className="profile-optional">{copy.optional}</span>
                      </label>
                      <select
                        id="trusted-person-coparent"
                        value={memberCoParentId}
                        onChange={(event) => setMemberCoParentId(event.target.value)}
                      >
                        <option value="">{copy.chooseSomeone}</option>
                        {familyMembers
                          .filter(
                            (candidate) =>
                              candidate.relation !== "Owner" &&
                              candidate.backendNodeId &&
                              candidate.backendNodeId !== "owner-node"
                          )
                          .map((candidate) => (
                            <option key={candidate.id} value={candidate.backendNodeId}>
                              {candidate.name} ({relationDisplayLabel(candidate.relation, copy)})
                            </option>
                          ))}
                      </select>
                    </div>
                  ) : null}

                  {memberRelation === "Grandchild" ? (
                    <div className="trusted-people-conditional">
                      <label className="field-label" htmlFor="trusted-person-branch">
                        {copy.branchUnder}
                      </label>
                      <select
                        id="trusted-person-branch"
                        value={memberChartParentId}
                        onChange={(event) => setMemberChartParentId(event.target.value)}
                      >
                        <option value="">
                          {childBranchOptions.length
                            ? copy.chooseChildBranch
                            : copy.addChildFirst}
                        </option>
                        {childBranchOptions.map((candidate) => (
                          <option key={candidate.id} value={candidate.backendNodeId}>
                            {candidate.name} ({relationDisplayLabel(candidate.relation, copy)})
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}

                  {memberRelation === "Spouse" || memberRelation === "Partner" ? (
                    <div className="trusted-people-conditional">
                      <label className="field-label" htmlFor="trusted-person-partner">
                        {copy.showBeside}
                      </label>
                      <select
                        id="trusted-person-partner"
                        value={memberPartnerId}
                        onChange={(event) => setMemberPartnerId(event.target.value)}
                      >
                        <option value="owner-node">{copy.besideYou}</option>
                        {partnerLinkOptions.map((candidate) => (
                          <option key={candidate.id} value={candidate.backendNodeId}>
                            {candidate.name} ({relationDisplayLabel(candidate.relation, copy)})
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}

                  <div className="trusted-people-inline-actions">
                    <button
                      type="button"
                      className="small-btn trusted-people-secondary"
                      onClick={() => setEntryStep(1)}
                    >
                      {copy.back}
                    </button>
                    <button
                      type="button"
                      className="small-btn trusted-people-next"
                      onClick={() => setEntryStep(3)}
                    >
                      {copy.next}
                    </button>
                  </div>
                </section>
              ) : null}

              {entryStep >= 3 ? (
                <section className="trusted-people-step-card">
                  <div className="trusted-people-step-card__head">
                    <span className="trusted-people-step-number">{copy.step3}</span>
                    <h3>{copy.step3Title}</h3>
                  </div>

                  <label className="trusted-people-toggle">
                    <input
                      type="checkbox"
                      checked={futureAccessIntent}
                      onChange={(event) => setFutureAccessIntent(event.target.checked)}
                    />
                    <span>{copy.mayReceiveFuture}</span>
                  </label>

                  <label className="field-label" htmlFor="trusted-person-email">
                    {copy.email}
                  </label>
                  <input
                    id="trusted-person-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    placeholder={copy.emailPlaceholder}
                    value={memberEmail}
                    onChange={(event) => setMemberEmail(event.target.value)}
                  />
                  <p className="trusted-people-hint">{copy.emailHelp}</p>

                  <div className="trusted-people-inline-actions">
                    <button
                      type="button"
                      className="small-btn trusted-people-secondary"
                      onClick={() => setEntryStep(2)}
                    >
                      {copy.back}
                    </button>
                    <button
                      type="button"
                      className="small-btn trusted-people-next"
                      onClick={() => setDetailsOpen((prev) => !prev)}
                      aria-expanded={detailsOpen}
                    >
                      {detailsOpen ? copy.hideDetails : copy.addMoreDetails}
                    </button>
                  </div>
                </section>
              ) : null}

              {entryStep >= 3 && detailsOpen ? (
                <section className="trusted-people-step-card trusted-people-step-card--optional">
                  <div className="trusted-people-step-card__head">
                    <span className="trusted-people-step-number">{copy.optionalSection}</span>
                    <h3>{copy.optionalTitle}</h3>
                  </div>
                  <div className="trusted-people-optional-grid">
                    <div>
                      <label className="field-label" htmlFor="trusted-person-birth-year">
                        {copy.birthYear} <span className="profile-optional">{copy.optional}</span>
                      </label>
                      <input
                        id="trusted-person-birth-year"
                        type="number"
                        inputMode="numeric"
                        min={1900}
                        max={new Date().getFullYear()}
                        placeholder={copy.birthYearPlaceholder(new Date().getFullYear() - 30)}
                        value={memberBirthYear}
                        onChange={(event) => setMemberBirthYear(event.target.value)}
                      />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="trusted-person-nickname">
                        {copy.nickname} <span className="profile-optional">{copy.optional}</span>
                      </label>
                      <input
                        id="trusted-person-nickname"
                        type="text"
                        placeholder={copy.nicknamePlaceholder}
                        value={memberNickname}
                        onChange={(event) => setMemberNickname(event.target.value)}
                      />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="trusted-person-pronouns">
                        {copy.pronouns} <span className="profile-optional">{copy.optional}</span>
                      </label>
                      <select
                        id="trusted-person-pronouns"
                        value={memberPronouns}
                        onChange={(event) => setMemberPronouns(event.target.value)}
                      >
                        <option value="">{copy.pronounsPlaceholder}</option>
                        <option value="he/him">{copy.pronounsOptions[0]}</option>
                        <option value="she/her">{copy.pronounsOptions[1]}</option>
                        <option value="they/them">{copy.pronounsOptions[2]}</option>
                        <option value="uses name only">{copy.pronounsOptions[3]}</option>
                        <option value="other">{copy.pronounsOptions[4]}</option>
                      </select>
                    </div>
                    <div>
                      <label className="field-label" htmlFor="trusted-person-identity-note">
                        {copy.personalContext} <span className="profile-optional">{copy.optional}</span>
                      </label>
                      <input
                        id="trusted-person-identity-note"
                        type="text"
                        placeholder={copy.personalContextPlaceholder}
                        value={memberIdentityNote}
                        onChange={(event) => setMemberIdentityNote(event.target.value)}
                      />
                    </div>
                  </div>
                  <label className="field-label" htmlFor="trusted-person-notes">
                    {copy.notes} <span className="profile-optional">{copy.optional}</span>
                  </label>
                  <textarea
                    id="trusted-person-notes"
                    rows={3}
                    placeholder={copy.notesPlaceholder}
                    value={memberSpecialNote}
                    onChange={(event) => setMemberSpecialNote(event.target.value)}
                  />
                </section>
              ) : null}

              {entryStep >= 3 ? (
                <div className="trusted-people-form-actions">
                  <button
                    type="button"
                    className="small-btn trusted-people-secondary"
                    onClick={resetDraft}
                  >
                    {copy.continueLater}
                  </button>
                  <button type="submit" disabled={!canSubmit}>
                    {copy.addPerson}
                  </button>
                </div>
              ) : null}

              {planLimitPrompt ? (
                <div className="trusted-people-plan-limit" role="alert">
                  <div className="trusted-people-plan-limit__copy">
                    <p className="trusted-people-plan-limit__eyebrow">{copy.trialUnlockEyebrow}</p>
                    <h4>{planLimitCopy?.title || copy.trialUnlockFallback}</h4>
                    <p>{planLimitPrompt.message || planLimitCopy?.body}</p>
                  </div>
                  <div className="trusted-people-plan-limit__actions">
                    <button type="button" onClick={onStartTrial}>
                      {planLimitCopy?.primaryCta || copy.startTrial}
                    </button>
                    <button
                      type="button"
                      className="small-btn trusted-people-secondary"
                      onClick={onOpenPricing}
                    >
                      {planLimitCopy?.secondaryCta || copy.seePlans}
                    </button>
                  </div>
                </div>
              ) : null}
            </form>
          </div>

          <aside className="trusted-people-sidebar" aria-label={copy.sidebarAria}>
            <div className="trusted-people-sidebar__head">
              <h3>{copy.sidebarTitle}</h3>
              <p>{copy.sidebarBody}</p>
            </div>

            {!peopleOnly.length ? (
              <div className="trusted-people-empty">
                <p className="trusted-people-empty__title">{copy.emptyTitle}</p>
                <p>{copy.emptyBody}</p>
                <button
                  type="button"
                  onClick={() => {
                    setEntryStep(1);
                    window.requestAnimationFrame(() => {
                      nameInputRef.current?.focus();
                    });
                  }}
                >
                  {copy.addSomeone}
                </button>
              </div>
            ) : (
              <div className="trusted-people-member-grid">
                {peopleOnly.map((member) => (
                  <TrustedPeopleMemberCard
                    key={member.id}
                    member={member}
                    locale={locale}
                    copy={copy}
                    isEditingEmail={editingFamilyEmailNodeId === member.backendNodeId}
                    editingFamilyEmailValue={editingFamilyEmailValue}
                    setEditingFamilyEmailValue={setEditingFamilyEmailValue}
                    familyEmailSavingId={familyEmailSavingId}
                    beginFamilyEmailEdit={beginFamilyEmailEdit}
                    cancelFamilyEmailEdit={cancelFamilyEmailEdit}
                    saveFamilyMemberEmail={saveFamilyMemberEmail}
                    familyInviteBusyId={familyInviteBusyId}
                    askToJoin={askToJoin}
                    resendInvite={resendInvite}
                    cancelInvite={cancelInvite}
                    requestDeleteFamilyMember={requestDeleteFamilyMember}
                  />
                ))}
              </div>
            )}
          </aside>
        </div>
      </div>

      <section className="trusted-people-trust" aria-label={copy.privacyAria}>
        <div>
          <p className="trusted-people-kicker">{copy.privacyKicker}</p>
          <h3>{copy.privacyTitle}</h3>
        </div>
        <ul className="trusted-people-trust-list">
          {copy.privacyPoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}
